import { useCallback, useRef } from "react";
import { ProvetContextType } from "../../../context/provetContext";
import { ProvetMonthModel, ProvetMonthModelBuilder } from "../../../types";
import { fetchAvailableTimes } from "../appointmentApi";
import { useCalendarState } from "./useCalendarState";
import { useRequestManager } from "./useRequestManager";

/** How long a change waits for a quicker next one before it applies (debounce) */
const UPDATE_DELAY_MS = 200;

export interface CalendarUpdateOptions {
  /** Load the month's times again even if they're loaded, e.g. when Step 3 reopens */
  refetch?: boolean;
  /** After loading, select the month's first or last day (month and year navigation) */
  selectDayOfMonth?: "first" | "last";
  /** If the selected day has no times, select the month's first day that has (first entry) */
  selectFirstAvailableDay?: boolean;
}

interface UseCalendarUpdaterParams {
  provetContext: ProvetContextType;
  calendarState: ReturnType<typeof useCalendarState>;
  requestManager: ReturnType<typeof useRequestManager>;
}

const isSameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/**
 * Loads the calendar's months and works out the selected day and its times.
 *
 * - A change is debounced, and a newer change aborts the request of an older one, so the
 *   last click always wins (observation 13).
 * - The first available date is asked for with every month, so an empty month always offers
 *   "Go to first available time" (observation 12). The server only searches for it when the
 *   month has no times.
 * - A selected vet with no times in the loaded month falls back to "any vet" (observation 14).
 * - A failure to load shows an error with "Try again" (`retry`), not "no available times"
 *   (observation 9).
 */
export function useCalendarUpdater({
  provetContext,
  calendarState,
  requestManager
}: UseCalendarUpdaterParams) {
  const department = provetContext?.bookingFlowState.formData?.selectedDepartment;
  const reason = provetContext?.bookingFlowState.formData?.selectedReason;
  const bufferTime = department?.online_booking_buffer_time || 0;

  // The latest update, repeated by "Try again"
  const lastUpdateRef = useRef<{ date: Date; options: CalendarUpdateOptions } | null>(
    null
  );

  // The first available date found for this clinic and treatment. While it is after a
  // requested month, that month is empty, so the server isn't asked to search again.
  const knownFirstAvailableRef = useRef<{ key: string; date: Date } | null>(null);
  const firstAvailableKey = `${department?.id ?? 0}/${reason?.id ?? 0}`;

  const loadMonth = useCallback(
    async (
      date: Date,
      signal: AbortSignal,
      refetch: boolean
    ): Promise<ProvetMonthModel> => {
      const monthModelBuilder = new ProvetMonthModelBuilder(date, bufferTime);
      const lastDateOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const known = knownFirstAvailableRef.current;
      // Reloading (Step 3 reopened, "Try again") asks again: the date may have been booked
      const reuseFirstAvailable =
        !refetch && known?.key === firstAvailableKey && known.date > lastDateOfMonth
          ? known.date
          : undefined;

      await monthModelBuilder.populateMonth(
        (startDate, endDate, includeFirstAvailableDate) =>
          fetchAvailableTimes({
            api: provetContext.api,
            startDate,
            endDate,
            departmentId: department?.id || 0,
            reasonId: reason?.id || 0,
            reasonDuration: reason?.duration || 0,
            bufferTime,
            includeFirstAvailableDate,
            abortSignal: signal
          }),
        !reuseFirstAvailable
      );

      const month = monthModelBuilder.build();

      if (reuseFirstAvailable) {
        return { ...month, firstAvailableDate: reuseFirstAvailable };
      }

      if (month.firstAvailableDate) {
        knownFirstAvailableRef.current = {
          key: firstAvailableKey,
          date: month.firstAvailableDate
        };
      }

      return month;
    },
    [provetContext.api, department, reason, bufferTime, firstAvailableKey]
  );

  const showLoadError = useCallback(
    async (date: Date, error: unknown) => {
      // The month's days, without times, so the grid matches the header
      const emptyMonthBuilder = new ProvetMonthModelBuilder(date, bufferTime);
      await emptyMonthBuilder.populateMonth(null);

      calendarState.loadedMonthRef.current = null;
      calendarState.setTimesLoadError(error);
      calendarState.setProvetMonth(emptyMonthBuilder.build());
      calendarState.setFirstAvailableDate(undefined);
      calendarState.setSelectedDate(date);
      calendarState.setAvailableAppointmentTimesForSelectedDay([]);
      calendarState.setTimesStatus("error");
    },
    [calendarState, bufferTime]
  );

  const applySelectedDate = useCallback(
    async (newSelectedDate: Date, options: CalendarUpdateOptions) => {
      if (!department || !reason) {
        // Nothing to load: don't leave a spinner behind
        calendarState.setTimesStatus("loaded");
        return;
      }

      let month = calendarState.loadedMonthRef.current;

      // A request still in flight would overwrite this selection when it lands, so it is
      // replaced by a request for this selection
      const fetchMonth =
        options.refetch ||
        !month ||
        !isSameMonth(month.firstDateOfMonth, newSelectedDate) ||
        requestManager.isRequestInFlight();

      if (fetchMonth) {
        const controller = requestManager.startRequest();
        calendarState.setTimesStatus("loading");

        try {
          month = await loadMonth(newSelectedDate, controller.signal, !!options.refetch);
        } catch (error) {
          if (controller.signal.aborted) {
            // A newer update took over
            return;
          }
          console.error("Error loading the available times:", error);
          requestManager.finishRequest(controller);
          await showLoadError(newSelectedDate, error);
          return;
        }

        if (controller.signal.aborted) {
          return;
        }
        requestManager.finishRequest(controller);

        calendarState.loadedMonthRef.current = month;
        calendarState.setProvetMonth(month);
        calendarState.setFirstAvailableDate(month.firstAvailableDate);

        // A vet with no times in this month falls back to "any vet"
        const previousVetId = calendarState.selectedVetIdRef.current;
        if (
          previousVetId > 0 &&
          !month.veterinarians.some(vet => vet.id === previousVetId)
        ) {
          calendarState.setSelectedVetId(0);
        }
      }

      if (!month) {
        return;
      }

      const loadedMonth = month;
      const vetId = calendarState.selectedVetIdRef.current;
      let finalSelectedDate = newSelectedDate;

      if (options.selectDayOfMonth) {
        finalSelectedDate = new Date(
          options.selectDayOfMonth === "last"
            ? loadedMonth.lastDateOfMonth
            : loadedMonth.firstDateOfMonth
        );
      }

      // On entry, select the month's first day with times if the selected day has none
      if (
        options.selectFirstAvailableDay &&
        loadedMonth.getAppointmentsForDate(finalSelectedDate, vetId).length === 0
      ) {
        const firstAvailableDay = loadedMonth.weeks
          .flatMap(week => week.days)
          .find(
            day =>
              isSameMonth(day.date, finalSelectedDate) &&
              loadedMonth.getAppointmentsForDate(day.date, vetId).length > 0
          );

        if (firstAvailableDay) {
          finalSelectedDate = firstAvailableDay.date;
        }
      }

      calendarState.setSelectedDate(finalSelectedDate);
      calendarState.setAvailableAppointmentTimesForSelectedDay(
        loadedMonth.getAppointmentsForDate(finalSelectedDate, vetId)
      );
      calendarState.setTimesStatus("loaded");
    },
    [department, reason, calendarState, requestManager, loadMonth, showLoadError]
  );

  /**
   * Selects a date, loading its month when needed. Debounced: a quicker next call replaces
   * this one.
   */
  const updateSelectedDate = useCallback(
    (newSelectedDate: Date, options: CalendarUpdateOptions = {}) => {
      lastUpdateRef.current = { date: newSelectedDate, options };
      requestManager.schedule(() => {
        applySelectedDate(newSelectedDate, options).catch(error =>
          console.error("Error updating selected date:", error)
        );
      }, UPDATE_DELAY_MS);
    },
    [requestManager, applySelectedDate]
  );

  /**
   * Shows the selected day's times for the selected vet right away, without waiting for the
   * debounce, so the list never shows the previous vet's times (observation 22).
   */
  const showTimesForSelectedVet = useCallback(
    (selectedDate: Date) => {
      // An update is waiting (debounce) or loading: it reads the vet when it applies, so it
      // is left to finish with its own options (e.g. "last day" of a previous month)
      if (requestManager.isScheduled() || requestManager.isRequestInFlight()) {
        return;
      }

      const month = calendarState.loadedMonthRef.current;

      if (!month || !isSameMonth(month.firstDateOfMonth, selectedDate)) {
        const lastUpdate = lastUpdateRef.current;
        updateSelectedDate(lastUpdate?.date ?? selectedDate, lastUpdate?.options);
        return;
      }

      calendarState.setAvailableAppointmentTimesForSelectedDay(
        month.getAppointmentsForDate(selectedDate, calendarState.selectedVetIdRef.current)
      );
    },
    [calendarState, requestManager, updateSelectedDate]
  );

  /** "Try again": repeats the latest update, loading its month again. */
  const retry = useCallback(() => {
    const lastUpdate = lastUpdateRef.current;

    if (!lastUpdate) {
      return;
    }

    calendarState.setTimesStatus("loading");
    updateSelectedDate(lastUpdate.date, { ...lastUpdate.options, refetch: true });
  }, [calendarState, updateSelectedDate]);

  return { updateSelectedDate, showTimesForSelectedVet, retry };
}
